import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Clock, Eye, ArrowRight, BookOpen, Globe, Loader2, Languages } from 'lucide-react';
import { useSEO } from '../utils/seo';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState('en');
  const [translating, setTranslating] = useState(false);
  const [translatedPosts, setTranslatedPosts] = useState({});
  const [translatedHeader, setTranslatedHeader] = useState(null);

  useSEO({
    title: 'Property Tips & Guides — Sell Without Estate Agent',
    description: 'Expert property advice for UK, India & Canada. Learn how to sell, buy, or rent without estate agent fees.',
    canonical: 'https://shopperden.com/blog',
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    const container = document.getElementById('soro-blog');
    if (!container) return;
    const params = new URLSearchParams(window.location.search);
    let url = 'https://app.trysoro.com/api/embed/24f1e95d-da9c-46ef-ae2f-7c2a572cb22c';
    if (params.get('post')) url += '?post=' + encodeURIComponent(params.get('post'));
    const script = document.createElement('script');
    script.src = url;
    container.appendChild(script);
    return () => { if (container) { while (container.firstChild) container.removeChild(container.firstChild); } };
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/blog/posts`);
      setPosts(res.data || []);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const translateContent = async (langCode) => {
    if (langCode === 'en') {
      setActiveLang('en');
      return;
    }

    if (translatedPosts[langCode]) {
      setActiveLang(langCode);
      return;
    }

    setTranslating(true);
    setActiveLang(langCode);

    try {
      // Translate header
      const headerRes = await axios.post(`${API_URL}/api/ai/translate`, {
        text: 'Knowledge Hub\nProperty Tips & Guides\nExpert advice on selling, buying and renting property without estate agent fees across UK, India and Canada.\nRead More',
        target_lang: langCode,
      });
      const headerLines = headerRes.data.translated.split('\n');
      setTranslatedHeader({
        hub: headerLines[0] || 'Knowledge Hub',
        title: headerLines[1] || 'Property Tips & Guides',
        subtitle: headerLines[2] || '',
        readMore: headerLines[3] || 'Read More',
      });

      // Translate posts (batch: title + excerpt together to save API calls)
      const batchText = posts.map(p => `TITLE: ${p.title}\nEXCERPT: ${p.excerpt || ''}`).join('\n---\n');
      const batchRes = await axios.post(`${API_URL}/api/ai/translate`, {
        text: batchText,
        target_lang: langCode,
      });

      const translatedBlocks = batchRes.data.translated.split('---');
      const mapped = {};
      posts.forEach((post, i) => {
        const block = (translatedBlocks[i] || '').trim();
        const titleMatch = block.match(/TITLE:\s*(.*)/i);
        const excerptMatch = block.match(/EXCERPT:\s*(.*)/is);
        mapped[post.slug] = {
          title: titleMatch ? titleMatch[1].trim() : post.title,
          excerpt: excerptMatch ? excerptMatch[1].trim() : (post.excerpt || ''),
        };
      });

      setTranslatedPosts(prev => ({ ...prev, [langCode]: mapped }));
      toast.success(`Translated to ${LANGUAGES.find(l => l.code === langCode)?.label}`);
    } catch (err) {
      toast.error('Translation failed. Please try again.');
      setActiveLang('en');
    } finally {
      setTranslating(false);
    }
  };

  const getPostText = (post, field) => {
    if (activeLang === 'en') return post[field];
    return translatedPosts[activeLang]?.[post.slug]?.[field] || post[field];
  };

  const headerText = activeLang !== 'en' && translatedHeader ? translatedHeader : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="blog-page">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-3 block uppercase font-semibold">
            {headerText?.hub || 'Knowledge Hub'}
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#1A1A1A] font-semibold">
            {headerText ? headerText.title : <>Property Tips & <span className="italic text-[#E07A3A]">Guides</span></>}
          </h1>
          <p className="text-[#1A1A1A]/50 mt-4 max-w-2xl mx-auto text-base">
            {headerText?.subtitle || 'Expert advice on selling, buying and renting property without estate agent fees across UK, India and Canada.'}
          </p>
        </div>

        {/* AI Translation Bar */}
        <div className="flex items-center justify-center gap-2 mb-10 flex-wrap" data-testid="blog-translate-bar">
          <div className="flex items-center gap-1.5 mr-2">
            <Languages className="h-4 w-4 text-[#E07A3A]" />
            <span className="text-xs tracking-widest uppercase text-slate-400 font-semibold">AI Translate:</span>
          </div>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => translateContent(lang.code)}
              disabled={translating}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold tracking-wider transition-all ${
                activeLang === lang.code
                  ? 'bg-[#E07A3A] text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-[#E07A3A] hover:text-[#E07A3A]'
              } ${translating && activeLang === lang.code ? 'animate-pulse' : ''}`}
              data-testid={`translate-${lang.code}`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
          {translating && (
            <div className="flex items-center gap-2 ml-2 text-[#E07A3A]">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs font-semibold tracking-wider">Translating...</span>
            </div>
          )}
        </div>

        {/* Soro Blog Embed */}
        <div id="soro-blog" className="mb-12" data-testid="soro-blog-embed"></div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length > 0 ? (
          <div className="space-y-8">
            {posts.map((post) => (
              <Link to={`/blog/${post.slug}`} key={post.slug} className="block group" data-testid={`blog-post-${post.slug}`}>
                <article className="luxury-card overflow-hidden hover:border-[#E07A3A]/30 transition-all duration-300">
                  <div className="flex flex-col md:flex-row">
                    {post.cover_image && (
                      <div className="md:w-72 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-6 flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {post.category && (
                          <span className="text-[10px] tracking-wider uppercase font-bold text-[#E07A3A] bg-[#E07A3A]/10 px-2 py-1">{post.category.replace(/-/g, ' ')}</span>
                        )}
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" />
                          {new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {post.views > 0 && (
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Eye className="h-3 w-3" />{post.views}
                          </span>
                        )}
                      </div>
                      <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold group-hover:text-[#E07A3A] transition-colors mb-2">
                        {getPostText(post, 'title')}
                      </h2>
                      <p className="text-slate-500 text-sm leading-relaxed mb-4">{getPostText(post, 'excerpt')}</p>
                      <span className="text-sm text-[#E07A3A] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        {headerText?.readMore || 'Read More'} <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        ) : (
          <div className="luxury-card p-16 text-center">
            <BookOpen className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">Coming Soon</h3>
            <p className="text-slate-400">Expert property guides and tips are being prepared. Check back soon!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
